import React, {useContext, useEffect, useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Button, Grid, Typography, useTheme} from "@mui/material";
import Divider from '@mui/material/Divider';
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {ExpandMore} from "@mui/icons-material";
import {FormAutocomplete, FormDateTimePicker, FormTextField} from '../../../common/form-fields';
import {
    AncillaryExceptionAssociationRequest,
    AncillaryExceptionPlanRequest,
    ExceptionPlanRequest,
    FormProductPlan
} from '../../../api/types/productPlan';
import {ancillariesAtom, exceptionPlanAtom, newCombinationAtom} from '../../../state/productPlan';
import {useRecoilState} from 'recoil';
import dayjs from 'dayjs';
import {useProductPlanRepository} from '../../../api/repositories/ProductPlanRepository';
import {useAncillaryExceptionRepository} from '../../../api/repositories/AncillaryExceptionRepository';
import {AncillaryExceptionSelectFE} from '../../../api/types/ancillaryException';
import {dateFormatterShort, fromAncillaryBeToFe, fromAncillaryExceptionBeToFe} from '../../../utils';
import {useAncillaryRepository} from '../../../api/repositories/AncillaryRepository';
import {AncillaryEntityResponse, AncillarySelectFE} from '../../../api/types/ancillary';
import {ExceptionListEntityResponse} from "../../../api/types/exceptionList";
import {SET_SNACKBAR} from "../../../constants";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {ProductCodeSelectFE} from "../../../api/types/productCode";

const ProductPlanException = () => {
    // Use State
    const [exceptionExpanded, setExceptionExpanded] = useState<boolean>(false);
    const [associableAndAssociateExceptions, setAssociableAndAssociateExceptions] = useState<AncillaryExceptionSelectFE[]>([])

    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Form Context
    const {watch, setValue, register, resetField, trigger} = useFormContext<FormProductPlan>();
    const watchAllForm = watch();

    //Use Repository Hooks
    const {
        createExceptionPlan,
        getAncillaryExceptionByExceptionList,
        createNewAncillaryException,
        putAssociateExceptions
    } = useProductPlanRepository();
    const {getAncillaryExceptionsByExceptionListAndCommercialCategory} = useAncillaryExceptionRepository();
    const {getAncillariesBySegmentId} = useAncillaryRepository();

    //Use Recoil
    const [exceptionPlan] = useRecoilState(exceptionPlanAtom);
    const [newCombination, setNewCombination] = useRecoilState(newCombinationAtom);
    const [ancillaries, setAncillaries] = useRecoilState(ancillariesAtom)

    const reloadPage = () => {
        window.location.reload()
    }

    const confirmCreateExceptionPlan = async () => {
        const formValues = watch();
        const requestBody: ExceptionPlanRequest = {
            productCode: formValues.productPlan.productCode?.code as string,
            locationGroupId: formValues.productPlan.locationGroup?.code as number,
            name: formValues.productPlan.exceptionPlan.newCombination.name as string,
            from: dayjs(formValues.productPlan.exceptionPlan.newCombination.from).toISOString(),
            to: dayjs(formValues.productPlan.exceptionPlan.newCombination.to).toISOString(),
            dealer: (watchAllForm.productPlan.productCode as ProductCodeSelectFE).dealer as string
        }
        const exceptionPlanResponse = await createExceptionPlan(requestBody)
        setNewCombination(exceptionPlanResponse)
        setValue("productPlan.exceptionPlan.viewCombination.name", exceptionPlanResponse.name)
        setValue("productPlan.exceptionPlan.viewCombination.from", dateFormatterShort(exceptionPlanResponse.from))
        setValue("productPlan.exceptionPlan.viewCombination.to", dateFormatterShort(exceptionPlanResponse.to))
        resetField("productPlan.exceptionPlan.newCombination.name")
        resetField("productPlan.exceptionPlan.newCombination.from")
        resetField("productPlan.exceptionPlan.newCombination.to")

        const getAssociableException = await getAncillaryExceptionsByExceptionListAndCommercialCategory({
            exceptionListId: exceptionPlanResponse.id,
            commercialCategoryId: formValues.productPlan.commercialCategory?.code as string
        })
        const ancillaryAssociableExceptionsFe: AncillaryExceptionSelectFE[] = fromAncillaryExceptionBeToFe(getAssociableException).sort((a: AncillaryExceptionSelectFE, b: AncillaryExceptionSelectFE) => a.label.localeCompare(b.label));

        const getAssociateException = await getAncillaryExceptionByExceptionList(exceptionPlanResponse.id);
        const ancillaryAssociateExceptionsFe: AncillaryExceptionSelectFE[] = fromAncillaryExceptionBeToFe(getAssociateException).sort((a: AncillaryExceptionSelectFE, b: AncillaryExceptionSelectFE) => a.label.localeCompare(b.label));

        setValue("productPlan.exceptionPlan.viewCombination.exception", ancillaryAssociateExceptionsFe)

        setAssociableAndAssociateExceptions([{
            code: 0,
            label: t("pricing:actions.selectAll")
        }, ...ancillaryAssociateExceptionsFe, ...ancillaryAssociableExceptionsFe])

        const ancillariesData: AncillaryEntityResponse[] = (await getAncillariesBySegmentId({
            commercialSegmentId: watch("productPlan.commercialSegment.code") as number,
            showOnlyActive: true,
            dealers: (watchAllForm.productPlan.productCode as ProductCodeSelectFE).dealer as string,
            legalEntities: (watchAllForm.productPlan.productCode as ProductCodeSelectFE).legalEntity as string,
        }));
        const ancillaryList: AncillarySelectFE[] = fromAncillaryBeToFe(ancillariesData);
        setAncillaries(ancillaryList);
    }

    const getAssociableAndAssociateException = async () => {

        const getExceptions = await getAncillaryExceptionsByExceptionListAndCommercialCategory({
            exceptionListId: (exceptionPlan as ExceptionListEntityResponse).id,
            commercialCategoryId: watch("productPlan.commercialCategory.code") as string
        })
        const ancillaryAssociableExceptionsFe: AncillaryExceptionSelectFE[] = fromAncillaryExceptionBeToFe(getExceptions).sort((a: AncillaryExceptionSelectFE, b: AncillaryExceptionSelectFE) => a.label.localeCompare(b.label));

        const getAssociateExceptions = await getAncillaryExceptionByExceptionList((exceptionPlan as ExceptionListEntityResponse).id);
        const ancillaryAssociateExceptionsFe: AncillaryExceptionSelectFE[] = fromAncillaryExceptionBeToFe(getAssociateExceptions).sort((a: AncillaryExceptionSelectFE, b: AncillaryExceptionSelectFE) => a.label.localeCompare(b.label));

        setValue("productPlan.exceptionPlan.viewCombination.exception", ancillaryAssociateExceptionsFe)

        setAssociableAndAssociateExceptions([{
            code: 0,
            label: t("pricing:actions.selectAll")
        }, ...ancillaryAssociateExceptionsFe, ...ancillaryAssociableExceptionsFe])
    }

    useEffect(() => {
        const populateExceptionFields = () => {
            setValue("productPlan.exceptionPlan.viewCombination.name", (exceptionPlan as ExceptionListEntityResponse).name)
            setValue("productPlan.exceptionPlan.viewCombination.from", dateFormatterShort((exceptionPlan as ExceptionListEntityResponse).from))
            setValue("productPlan.exceptionPlan.viewCombination.to", dateFormatterShort((exceptionPlan as ExceptionListEntityResponse).to))
        }

        const getAncillaries = async () => {
            const ancillariesData: AncillaryEntityResponse[] = (await getAncillariesBySegmentId({
                commercialSegmentId: watch("productPlan.commercialSegment.code") as number,
                dealers: (watchAllForm.productPlan.productCode as ProductCodeSelectFE).dealer as string,
                legalEntities: (watchAllForm.productPlan.productCode as ProductCodeSelectFE).legalEntity as string,
                showOnlyActive: true
            }));
            const ancillaryList: AncillarySelectFE[] = fromAncillaryBeToFe(ancillariesData);
            setAncillaries(ancillaryList);
        }

        if (exceptionPlan) {
            populateExceptionFields()
            getAssociableAndAssociateException()
            getAncillaries()
        }
    }, [exceptionPlan])

    const handleChangeAncillaryExceptions = (input: AncillaryExceptionSelectFE[]) => {
        const value: AncillaryExceptionSelectFE[] =
            // è stato cliccato "selectAll"
            input.find((el: AncillaryExceptionSelectFE) => el.code === 0)
            &&
            // tra i vecchi valori non c'è "selectAll
            !watch("productPlan.exceptionPlan.viewCombination.exception")?.find((el: AncillaryExceptionSelectFE) => el.code === 0)
                ?
                // applica il "selectAll
                (associableAndAssociateExceptions as AncillaryExceptionSelectFE[]).filter((el: AncillaryExceptionSelectFE) => el.code !== 0)
                :
                // setta solo locations selezionati
                input.filter(el => el.code !== 0);
        setValue("productPlan.exceptionPlan.viewCombination.exception", value);
    };

    const createAndAddNewException = async () => {
        const validateAncillary = await trigger("productPlan.exceptionPlan.newException.ancillary")
        const validateExceptionName = await trigger("productPlan.exceptionPlan.newException.exceptionName")
        if (validateAncillary && validateExceptionName) {
            const formValues = watch();
            const requestBody: AncillaryExceptionPlanRequest = {
                name: formValues.productPlan.exceptionPlan.newException.exceptionName as string,
                exceptionListId: (exceptionPlan as ExceptionListEntityResponse).id as number,
                baseAncillaryId: formValues.productPlan.exceptionPlan.newException.ancillary?.code as number,
                commercialCategoryId: formValues.productPlan.commercialCategory?.code as string,
            }
            try {
                await createNewAncillaryException(requestBody);
                resetField("productPlan.exceptionPlan.newException.ancillary")
                resetField("productPlan.exceptionPlan.newException.exceptionName")
                await getAssociableAndAssociateException()
                dispatchGlobalData(SET_SNACKBAR.SUCCESS)
            } catch (e) {
            }
        }
    }

    const associateExceptions = async () => {
        const formValues = watch();
        const requestBody: AncillaryExceptionAssociationRequest = {
            exceptionListId: (exceptionPlan as ExceptionListEntityResponse).id as number,
            ancillaryExceptionIds: formValues.productPlan.exceptionPlan.viewCombination.exception.map(el => el.code as number)
        }
        await putAssociateExceptions(requestBody)
        dispatchGlobalData(SET_SNACKBAR.SUCCESS)
        reloadPage()
    }

    return (
        <Accordion expanded={exceptionExpanded} onChange={(e, expanded) => setExceptionExpanded(expanded)}>
            <AccordionSummary expandIcon={<ExpandMore/>}>
                <Typography sx={{color: theme.palette.primary.dark}}>
                    {"EXCEPTION PLAN"}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                {!exceptionPlan && !newCombination ?
                    <Grid container rowSpacing={2} columnSpacing={2}>
                        <Grid item xs={12} md={12}>
                            <Typography sx={{color: theme.palette.primary.dark}}>
                                {`${t("pricing:productPlan.new")}`}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {/* COMBINATION */}
                            <FormTextField
                                {...register("productPlan.exceptionPlan.newCombination.name")}
                                label={`${t("pricing:productPlan.combinationName")}*`}
                                type={"text"}
                                readOnly={!watchAllForm.productPlan.locationGroup}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {/* FROM */}
                            <FormDateTimePicker
                                {...register("productPlan.exceptionPlan.newCombination.from")}
                                label={`${t("pricing:ancillary.form.labels.reservationFrom")}*`}
                                minDate={dayjs()}
                                readOnly={!watchAllForm.productPlan.exceptionPlan.newCombination.name}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {/* TO */}
                            <FormDateTimePicker
                                {...register("productPlan.exceptionPlan.newCombination.to")}
                                label={`${t("pricing:ancillary.form.labels.reservationTo")}*`}
                                minDate={watch("productPlan.exceptionPlan.newCombination.from")}
                                readOnly={!watchAllForm.productPlan.exceptionPlan.newCombination.from}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}
                              sx={{display: "flex", justifyContent: "flex-end", alignItems: "flex-end",}}>
                            <Button
                                color={"accent"}
                                type={"button"}
                                onClick={confirmCreateExceptionPlan}
                                sx={{border: `1px solid ${theme.palette.accent.main}`}}
                            >
                                {t("pricing:actions.confirm")}
                            </Button>
                        </Grid>
                    </Grid>
                    :
                    <>
                        <Grid container rowSpacing={2} columnSpacing={2}>
                            <Grid item xs={12} md={6}>
                                {/* COMBINATION */}
                                <FormTextField
                                    {...register("productPlan.exceptionPlan.viewCombination.name")}
                                    label={`${t("pricing:productPlan.combinationName")}`}
                                    type={"text"}
                                    readOnly={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {/* EXCEPTION ASSOCIATE */}
                                <FormAutocomplete
                                    multiple={true}
                                    {...register("productPlan.exceptionPlan.viewCombination.exception")}
                                    label={`${t("pricing:productPlan.exceptions")}*`}
                                    options={associableAndAssociateExceptions as AncillaryExceptionSelectFE[]}
                                    handleChange={handleChangeAncillaryExceptions}
                                    isOptionEqualToValue={(option: AncillaryExceptionSelectFE, value: AncillaryExceptionSelectFE) => option?.code === value?.code}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                {/* FROM */}
                                <FormTextField
                                    {...register("productPlan.exceptionPlan.viewCombination.from")}
                                    label={`${t("pricing:ancillary.form.labels.reservationFrom")}`}
                                    readOnly={true}
                                    type={"text"}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                {/* TO */}
                                <FormTextField
                                    {...register("productPlan.exceptionPlan.viewCombination.to")}
                                    label={`${t("pricing:ancillary.form.labels.reservationTo")}`}
                                    readOnly={true}
                                    type={"text"}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}
                                  sx={{display: "flex", justifyContent: "flex-end", alignItems: "flex-end",}}>
                                <Button
                                    color={"accent"}
                                    type={"button"}
                                    onClick={associateExceptions}
                                    sx={{border: `1px solid ${theme.palette.accent.main}`}}
                                >
                                    {t("pricing:actions.confirm")}
                                </Button>
                            </Grid>
                        </Grid>
                        <Divider style={{marginTop: "20px"}}>{t("pricing:productPlan.divider")}</Divider>
                        <Grid container rowSpacing={2} columnSpacing={2}>
                            <Grid item xs={12} md={3}>
                                {/* ANCILLARY */}
                                <FormAutocomplete
                                    {...register("productPlan.exceptionPlan.newException.ancillary")}
                                    label={`${t("pricing:productPlan.ancillary")}`}
                                    options={ancillaries}
                                    handleChange={async (value: AncillarySelectFE) => {
                                        setValue("productPlan.exceptionPlan.newException.ancillary", value)
                                        await trigger("productPlan.exceptionPlan.newException.ancillary")
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                {/* EXCEPTION NAME */}
                                <FormTextField
                                    {...register("productPlan.exceptionPlan.newException.exceptionName")}
                                    label={`${t("pricing:productPlan.exceptionName")}`}
                                    type={"text"}
                                    handleChange={async (value: string) => {
                                        setValue("productPlan.exceptionPlan.newException.exceptionName", value)
                                        await trigger("productPlan.exceptionPlan.newException.exceptionName")
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}
                                  sx={{display: "flex", justifyContent: "flex-end", alignItems: "flex-end",}}>
                                <Button
                                    color={"accent"}
                                    type={"button"}
                                    onClick={createAndAddNewException}
                                    sx={{border: `1px solid ${theme.palette.accent.main}`}}
                                >
                                    {t("pricing:actions.confirm")}
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                }
            </AccordionDetails>
        </Accordion>
    );
}

export default ProductPlanException;